/**
 * Trimble Connect public share links.
 *
 * A share link carries a token that can be exchanged, without any login, for a scoped
 * "public" access token covering exactly the shared objects. That makes a share link the
 * one way to open project data with no Trimble Identity session and no registered
 * application — useful for demos, for read-only viewers, and for testing outside Connect.
 *
 * The links appear in two shapes:
 *   https://app21.connect.trimble.com/tc/api/2.0/s/<token>
 *   https://web.connect.trimble.com#/project/<id>/detailviewer/<fileId>/…?stoken=<token>&origin=app21.connect.trimble.com
 */
export interface ParsedShareLink {
  token: string;
  /** Core API base URL implied by the link's host, when it names one. */
  baseUrl?: string;
}

export function parseShareLink(input: string): ParsedShareLink | null {
  const text = input.trim();
  if (!text) return null;

  // A bare token: the opaque part of a share URL, no scheme or slashes.
  if (!text.includes("/") && !text.includes(":") && text.length >= 20) {
    return { token: text };
  }

  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }

  // The web-app form keeps everything after the fragment marker, so the query string has
  // to be read out of the hash rather than from `url.searchParams`.
  const hashQuery = url.hash.includes("?")
    ? new URLSearchParams(url.hash.slice(url.hash.indexOf("?") + 1))
    : null;
  const stoken = url.searchParams.get("stoken") ?? hashQuery?.get("stoken");
  const origin = url.searchParams.get("origin") ?? hashQuery?.get("origin");

  if (stoken) {
    return { token: stoken, ...(origin ? { baseUrl: `https://${origin}/tc/api/2.0` } : {}) };
  }

  const match = /\/tc\/api\/(?:2\.\d\/)?s\/([^/?#]+)/.exec(url.pathname);
  if (match) {
    return { token: match[1], baseUrl: `${url.origin}/tc/api/2.0` };
  }
  return null;
}
